import { ColumnConfig, ColumnsObjectConfig, WidgetTemplateColumnWithConfig } from '@/types/dashboard'
import { ElTable } from 'element-ui/types/table'
import { isRTL } from '@/composables/useDirection'

const COL_MIN_WIDTH = 70
const WIDE_COL_WIDTH = 100
const commentNodeType = 8
const maxAttempts = 25
const intervalTime = 1000
const DBLCLICK_DELAY = 300
const ALIGNMENT_CLASSES = ['is-left', 'is-center', 'is-right']

function temporarilyRemoveAlignmentClasses(cell: HTMLElement): () => void {
    const removedClasses: string[] = []
    
    ALIGNMENT_CLASSES.forEach(cls => {
        if (cell.classList.contains(cls)) {
            cell.classList.remove(cls)
            removedClasses.push(cls)
        }
    })
    
    return removedClasses.length === 0 
    ? () => {}
    : () => removedClasses.forEach(cls => cell.classList.add(cls))
}


interface tableColumnWidthConfig {
    maxColumnBlockWidth: number
}
type tableStructure = Record<string, tableColumnWidthConfig>
interface ColumnsConfigGenerationOptions {
    autoLabelMode?: boolean
}
const listenerOptions: AddEventListenerOptions = { capture: true }

let lastClickTime = 0
export function useTableColumnUpdater (onColumnDblClick?: (prop: string, newWidth: number) => void) {
    let Table: undefined | ElTable
    let cleanupHeaderListener: (() => void) | undefined;
    let activeCheckInterval: ReturnType<typeof setInterval> | null = null
    const onRefDefined = (ref: ElTable) => {
        Table = ref
        if (Table) {

            const tableEl = Table.$el as HTMLElement
            const headerWrapper = tableEl?.querySelector<HTMLElement>('.el-table__header-wrapper thead')
            if (!headerWrapper) return

            const onClick = (e: MouseEvent) => {
                const th = (e.target as HTMLElement).closest('th')
                if (!th) return

                const propElement = th?.querySelector('[data-prop]')
                const columnProp = propElement?.getAttribute('data-prop')
                if (!columnProp) return

                // Check if near the resize edge
                // RTL: left edge, LTR: right edge
                const rect = th.getBoundingClientRect()
                const nearEdge = isRTL.value
                    ? (e.clientX - rect.left) <= 7
                    : (rect.right - e.clientX) <= 7

                if (!nearEdge) return

                // Stop propagation when near resize edge to prevent sort
                e.preventDefault()
                e.stopImmediatePropagation()
                e.stopPropagation()

                // Check for double-click timing
                const now = Date.now()
                if (now - lastClickTime > DBLCLICK_DELAY) {
                    lastClickTime = now
                    return
                }

                // Double-click detected - do the auto-resize
                const columnIndex = th.cellIndex
                const tableBody = tableEl.querySelector('tbody')
                if (!tableBody) {
                    return
                }
                const tableRows = Array.from(tableBody?.childNodes).filter(item => item.nodeType !== commentNodeType)

                const maxWidth = getMaxColumnWidth(tableRows, columnIndex)

                onColumnDblClick?.(columnProp, maxWidth)
            }

                headerWrapper.addEventListener('click', onClick, listenerOptions)
                cleanupHeaderListener = () => headerWrapper.removeEventListener('click', onClick, listenerOptions)
        }
    }

    const checkTableRows = () => {
        return new Promise<Node[]>((resolve, reject) => {
            let attempts = 0

            const tableElement = Table?.$el
            if (!tableElement) {
                return reject()
            }

            const intervalId = setInterval(() => {
                const tableBody = tableElement.querySelector('tbody')

                if (tableBody && tableBody.childNodes.length) {
                    const tableRows = Array.from(tableBody.childNodes).slice(0,10).filter(item => item.nodeType !== commentNodeType)
                    if (tableRows.length) {
                        clearInterval(intervalId)
                        activeCheckInterval = null
                        resolve(tableRows)
                    }
                }

                attempts++
                if (attempts >= maxAttempts) {
                    clearInterval(intervalId)
                    activeCheckInterval = null
                    reject(new Error('no table rows default table config will be generated'))
                }
            }, intervalTime)
            activeCheckInterval = intervalId
        })
    }

    const generateDefaultConfigObject = (columns: WidgetTemplateColumnWithConfig[], columnsConfig: ColumnsObjectConfig) => {
        if (Object.values(columnsConfig).length) {
            return columnsConfig
        }
        const columnsObjectConfig: Record<string, ColumnConfig> = {}
        columns.forEach((column, i) => {
            columnsObjectConfig[column.prop] = {
                hidden: false,
                width: 300,
                showIcon: false,
                showShortTextLabel: false,
                showFullTextLabel: true,
                order: i
            }
        })
        return columnsObjectConfig
    }

    const generateColumnsConfigObject = (
        tableRows: Node[],
        columns: WidgetTemplateColumnWithConfig[],
        columnsConfig: ColumnsObjectConfig = {},
        options: ColumnsConfigGenerationOptions = {}
    ) => {
        const tableStructure: tableStructure = {}
        const restoreFunctions: (() => void)[] = []

        // Calculate max column block widths
        tableRows.forEach((row) => {
            if (!(row instanceof Element) || !row.children) return

            Array.from(row.children).forEach((child, i) => {
                const columnKey = `column${i}`
                
                const restore = temporarilyRemoveAlignmentClasses(child as HTMLElement)
                restoreFunctions.push(restore)

                const firstChild = child.firstChild
                let blockWidth = 0

                if (firstChild instanceof HTMLElement) {
                    blockWidth = firstChild.offsetWidth
                }

                if (!tableStructure[columnKey]) {
                    tableStructure[columnKey] = { maxColumnBlockWidth: COL_MIN_WIDTH }
                }

                tableStructure[columnKey].maxColumnBlockWidth = Math.max(
                    tableStructure[columnKey].maxColumnBlockWidth,
                    blockWidth
                )
            })
        })

        restoreFunctions.forEach(restore => restore())

        const columnsObjectConfig: ColumnsObjectConfig = { ...columnsConfig }

        columns.forEach((column, i: number) => {
            const columnKey = `column${i}`
            const columnWidth = tableStructure[columnKey]?.maxColumnBlockWidth || COL_MIN_WIDTH
            const isNarrowColumn = columnWidth <= WIDE_COL_WIDTH
            const { iconLabel, shortTextLabel } = column.label || {}
            const autoShowIcon = isNarrowColumn && !!iconLabel
            const autoShowShortTextLabel = isNarrowColumn && !!shortTextLabel && !iconLabel
            const autoShowFullTextLabel = !(isNarrowColumn && (iconLabel || shortTextLabel))

            columnsObjectConfig[column.prop] = {
                ...columnsConfig[column.prop],
                hidden: columnsConfig[column.prop]?.hidden || false,
                width: columnWidth,
                order: columnsConfig[column.prop]?.order || i,
                showIcon: options.autoLabelMode
                    ? autoShowIcon
                    : columnsConfig[column.prop]?.showIcon ?? false,
                showShortTextLabel: options.autoLabelMode
                    ? autoShowShortTextLabel
                    : columnsConfig[column.prop]?.showShortTextLabel ?? false,
                showFullTextLabel: options.autoLabelMode
                    ? autoShowFullTextLabel
                    : columnsConfig[column.prop]?.showFullTextLabel ?? true
            }
        })

        return columnsObjectConfig
    }

    const getColumnsConfig = async (
        columns: WidgetTemplateColumnWithConfig[],
        columnsConfigObject: ColumnsObjectConfig = {},
        options: ColumnsConfigGenerationOptions = {}
    ) => {
        try {
            const tableRows = await checkTableRows()
            return generateColumnsConfigObject(tableRows, columns, columnsConfigObject, options)
        } catch (error) {
            console.error(error.message)
            return generateDefaultConfigObject(columns, columnsConfigObject)
        }
    }

    return {
        onRefDefined,
        getColumnsConfig,
        cleanUp: () => {
            cleanupHeaderListener?.()
            if (activeCheckInterval !== null) {
                clearInterval(activeCheckInterval)
                activeCheckInterval = null
            }
        }
    }
}

function getMaxColumnWidth(tableRows: ChildNode[], columnIndex: number): number {
    let maxWidth = COL_MIN_WIDTH
    const restoreFunctions: (() => void)[] = []

    tableRows.forEach((row) => {
        if (!(row instanceof Element) || !row.children) return

        const cell = row.children[columnIndex] as HTMLElement | undefined
        if (!cell) return

        const restore = temporarilyRemoveAlignmentClasses(cell)
        restoreFunctions.push(restore)

        const firstChild = cell.firstChild
        let blockWidth = 0

        if (firstChild instanceof HTMLElement) {
            blockWidth = firstChild.offsetWidth
        }

        maxWidth = Math.max(maxWidth, blockWidth)
    })

    restoreFunctions.forEach(restore => restore())

    return maxWidth
}
